import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Button,
  TextField,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel,
  Chip
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import axios from 'axios';
import { appConfig } from '../../config/app';
import { useApp } from '../../context/AppContext';
import ImageUpload from '../../components/common/ImageUpload';

const AppWebsiteNotifications = () => {
  const { actions } = useApp();
  const topic = 'NEPTUNE';
  const MAX_SCHEDULE_DAYS = 15;

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [cancellingId, setCancellingId] = useState('');
  const [targetedMode, setTargetedMode] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [mobilePreview, setMobilePreview] = useState(null);

  const [form, setForm] = useState({
    title: '',
    body: ''
  });

  const maxScheduleAtLocal = useMemo(() => {
    const d = new Date(Date.now() + (MAX_SCHEDULE_DAYS * 24 * 60 * 60 * 1000));
    return d.toISOString().slice(0, 16);
  }, []);

  const minScheduleAtLocal = useMemo(() => new Date().toISOString().slice(0, 16), []);

  const fetchSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const res = await axios.get(`${appConfig.api.baseURL}/notifications/topics/${topic}/schedules`, {
        params: { limit: 50 }
      });
      setScheduledNotifications(res.data?.data?.schedules || []);
    } catch (e) {
      console.error('Failed to load scheduled notifications:', e);
      actions.showSnackbar('Failed to load scheduled notifications', 'error');
    } finally {
      setLoadingSchedules(false);
    }
  };

  const previewUploadedMobiles = async (file) => {
    if (!file) return;
    if (!file.name?.toLowerCase().endsWith('.xlsx')) {
      actions.showSnackbar('Only .xlsx files are supported', 'warning');
      return;
    }

    setUploadingFile(true);
    setMobilePreview(null);
    setUploadedFileName(file.name);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${appConfig.api.baseURL}/notifications/topics/${topic}/upload-users/preview`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data?.success) {
        setMobilePreview(res.data?.data || null);
        actions.showSnackbar(res.data?.message || 'Upload preview ready', 'success');
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to preview uploaded users', 'error');
      }
    } catch (e) {
      console.error('Failed to preview uploaded users:', e);
      actions.showSnackbar(e.response?.data?.message || 'Failed to preview uploaded users', 'error');
      setUploadedFileName('');
    } finally {
      setUploadingFile(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      actions.showSnackbar('Title and message are required', 'warning');
      return;
    }

    if (targetedMode) {
      if (!mobilePreview?.mobiles?.length) {
        actions.showSnackbar('Upload .xlsx and wait for preview before sending', 'warning');
        return;
      }
    } else if (scheduleEnabled) {
      if (!scheduledAt) {
        actions.showSnackbar('Please select schedule date and time', 'warning');
        return;
      }

      const when = new Date(scheduledAt);
      if (Number.isNaN(when.getTime())) {
        actions.showSnackbar('Invalid schedule date and time', 'warning');
        return;
      }

      const now = Date.now();
      const max = now + (MAX_SCHEDULE_DAYS * 24 * 60 * 60 * 1000);
      if (when.getTime() <= now) {
        actions.showSnackbar('Schedule time must be in the future', 'warning');
        return;
      }
      if (when.getTime() > max) {
        actions.showSnackbar(`You can schedule only up to ${MAX_SCHEDULE_DAYS} days`, 'warning');
        return;
      }
    }

    setSending(true);
    setResult(null);
    try {
      const endpoint = targetedMode
        ? `${appConfig.api.baseURL}/notifications/topics/${topic}/push-by-mobiles`
        : (scheduleEnabled
            ? `${appConfig.api.baseURL}/notifications/topics/${topic}/schedule`
            : `${appConfig.api.baseURL}/notifications/topics/${topic}/push`);

      const payload = targetedMode
        ? {
            title: form.title.trim(),
            body: form.body.trim(),
            mobiles: mobilePreview.mobiles
          }
        : (scheduleEnabled
            ? {
                title: form.title.trim(),
                body: form.body.trim(),
                imageUrl: imageUrl || undefined,
                scheduledAt: new Date(scheduledAt).toISOString()
              }
            : {
                title: form.title.trim(),
                body: form.body.trim(),
                imageUrl: imageUrl || undefined
              });

      const res = await axios.post(endpoint, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      setResult(res.data);
      if (res.data?.success) {
        actions.showSnackbar(
          res.data.message || (scheduleEnabled ? 'Notification scheduled' : 'Notification sent'),
          'success'
        );
        if (scheduleEnabled) {
          setScheduledAt('');
          setScheduleEnabled(false);
          fetchSchedules();
        }
        if (targetedMode) {
          setMobilePreview(null);
          setUploadedFileName('');
        }
      } else {
        // Avoid snackbar loops in case auth/session is invalid
      }
    } catch (e) {
      console.error('Failed to send/schedule topic push:', e);
      setResult(e.response?.data || { success: false, message: e.message });
    } finally {
      setSending(false);
    }
  };

  const cancelSchedule = async (id) => {
    if (!id) return;
    setCancellingId(id);
    try {
      const res = await axios.post(
        `${appConfig.api.baseURL}/notifications/schedules/${id}/cancel`,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.data?.success) {
        actions.showSnackbar('Scheduled notification cancelled', 'success');
        fetchSchedules();
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to cancel schedule', 'error');
      }
    } catch (e) {
      console.error('Failed to cancel scheduled notification:', e);
      actions.showSnackbar(e.response?.data?.message || 'Failed to cancel schedule', 'error');
    } finally {
      setCancellingId('');
    }
  };

  const getStatusColor = (status) => {
    if (status === 'pending') return 'warning';
    if (status === 'sent') return 'success';
    if (status === 'failed') return 'error';
    if (status === 'cancelled') return 'default';
    return 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            App &amp; Website Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Send Firebase push notifications to the customer-app topic.
          </Typography>
        </Box>
      </Box>

      <Card>
        <CardHeader
          title="Send push notification"
        />
        <CardContent>
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Message"
              value={form.body}
              onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
              fullWidth
              required
              multiline
              rows={4}
            />

            <ImageUpload
              images={imageUrl ? [imageUrl] : []}
              onImagesChange={(urls) => setImageUrl(urls?.[0] || '')}
              uploadPath="notifications/topic"
              maxImages={1}
              label="Upload image (optional)"
              helperText="Optional image for Android push notification."
              multiple={false}
              showPreviews={true}
            />

            <FormControlLabel
              control={(
                <Checkbox
                  checked={targetedMode}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setTargetedMode(checked);
                    if (checked) {
                      setScheduleEnabled(false);
                    }
                  }}
                />
              )}
              label="Send only to uploaded mobile users (.xlsx)"
            />

            {targetedMode && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'info.light',
                  bgcolor: 'info.50',
                  borderRadius: 2,
                  p: 2
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Button variant="outlined" component="label" disabled={uploadingFile}>
                    {uploadingFile ? 'Processing...' : 'Upload .xlsx'}
                    <input
                      type="file"
                      hidden
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        previewUploadedMobiles(file);
                        e.target.value = '';
                      }}
                    />
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {uploadedFileName || 'No file selected'}
                  </Typography>
                </Stack>

                {mobilePreview && (
                  <Box sx={{ mt: 1.5 }}>
                    <Alert severity={mobilePreview.targetUserCount > 0 ? 'success' : 'warning'}>
                      Notification will be sent to <b>{mobilePreview.targetUserCount}</b> users.
                    </Alert>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Uploaded: {mobilePreview.uploadedCount} | Valid mobiles: {mobilePreview.validMobileCount} | No user: {mobilePreview.noUserCount} | No deviceToken: {mobilePreview.noDeviceTokenCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <FormControlLabel
              control={(
                <Checkbox
                  checked={scheduleEnabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (targetedMode && checked) return;
                    setScheduleEnabled(checked);
                  }}
                  disabled={targetedMode}
                />
              )}
              label={`Schedule notification (max ${MAX_SCHEDULE_DAYS} days)`}
            />

            {scheduleEnabled && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'primary.light',
                  bgcolor: 'primary.50',
                  borderRadius: 2,
                  p: 2
                }}
              >
                <TextField
                  label="Schedule date and time"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  required
                  sx={{
                    width: { xs: '100%', md: '40%' },
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper'
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: minScheduleAtLocal,
                    max: maxScheduleAtLocal
                  }}
                  helperText={`Choose a date/time within next ${MAX_SCHEDULE_DAYS} days.`}
                />
              </Box>
            )}

            <Divider />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={send}
                disabled={sending || (targetedMode && !mobilePreview?.targetUserCount)}
              >
                {sending ? (scheduleEnabled ? 'Scheduling...' : 'Sending...') : (scheduleEnabled ? 'Schedule' : 'Send')}
              </Button>
            </Stack>

            {result && (
              <Alert severity={result.success ? 'success' : 'error'}>
                {result.message || (result.success ? 'Sent' : 'Failed')}
                {result?.data?.messageId && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" component="div">
                      Message ID: {result.data.messageId}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardHeader title="Scheduled Notifications" />
        <CardContent>
          {loadingSchedules ? (
            <Typography variant="body2" color="text.secondary">Loading scheduled notifications...</Typography>
          ) : scheduledNotifications.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No scheduled notifications found.</Typography>
          ) : (
            <Stack spacing={1.5}>
              {scheduledNotifications.map((item) => (
                <Box
                  key={item.id}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {item.body}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Scheduled: {item.scheduledAt ? new Date(item.scheduledAt).toLocaleString() : '-'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={item.status || 'unknown'}
                        color={getStatusColor(item.status)}
                      />
                      {item.status === 'pending' && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => cancelSchedule(item.id)}
                          disabled={cancellingId === item.id}
                        >
                          {cancellingId === item.id ? 'Cancelling...' : 'Cancel'}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AppWebsiteNotifications;

